/**
 * SuperAdmin Overview — refined (visual theme preserved).
 *
 * Reads (existing endpoints, no backend changes):
 *   GET /api/superadmin/dashboard/stats
 *     → { users:{total,active,blocked},
 *         inventory:{total_components,total_stock_quantity,low_stock_count,total_exchanges,total_returns},
 *         orders:{total,pending_admin,pending_superadmin,approved},
 *         recent_activities:[{id,user_id,user_name,action,details,timestamp,...}, ...] }
 *   GET /api/superadmin/pending-approvals  → orders with status "pending" (admin + reassigned)
 *   GET /api/reports/overdue-defaulters    → { count, students_affected, cases[] }
 *   GET /api/superadmin/users              → used to resolve CURRENT user names in Recent Activity
 *                                            (activity_logs snapshot the name at write-time, so
 *                                             stale names persist after a profile edit).
 *
 * Audit transparency: operational events (inventory / exchange / order / return /
 * issuance / waitlist / user) are surfaced first in the Overview mini-feed. Login,
 * logout and OTP events are COUNTED and still accessible via the "View all" link
 * to the Activities page — nothing is deleted or concealed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertOctagon, Package, Users, ClipboardList, Download, Clock, Activity,
  ArrowRight, Repeat2,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const numFmt = (n) => new Intl.NumberFormat().format(Number(n || 0));
const STALE_MS = 24 * 60 * 60 * 1000;

// Compact human time: "just now · 2 min · 3 h · yesterday · 4 d".
const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  if (diff < 2 * 86_400_000) return 'yesterday';
  if (diff < 14 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d ago`;
  return d.toLocaleDateString();
};

// Full absolute timestamp — used as the <time title> tooltip and as the
// second line for the top operational event. Local tz, deterministic format.
const absTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const actionLabel = (a) => {
  if (!a) return '';
  return String(a).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Operational = anything that materially changes state. Login / logout /
// OTP requests are informational housekeeping — demoted but still visible
// via the Activities page.
const AUX_ACTIONS = new Set(['login', 'logout', 'otp_requested', 'otp_verified']);
const isOperational = (a) => a && !AUX_ACTIONS.has(a);

export default function OverviewPage({ ctx }) {
  const { showToast, goto } = ctx;
  const [stats, setStats] = useState({});
  const [pending, setPending] = useState([]);
  const [defaulters, setDefaulters] = useState({ count: 0, students_affected: 0, cases: [], grace_days: 7 });
  const [userMap, setUserMap] = useState({});   // id → { full_name, username }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, d, u] = await Promise.all([
        apiClient.get(endpoints.dashboard.superadminStats),
        apiClient.get(endpoints.orders.superadminPending),
        apiClient.get(endpoints.reports.overdueDefaultersJson),
        apiClient.get(endpoints.users.superadminList).catch(() => ({ data: [] })),
      ]);
      setStats(s.data || {});
      setPending(Array.isArray(p.data) ? p.data : []);
      setDefaulters(d.data || { count: 0, students_affected: 0, cases: [], grace_days: 7 });
      const map = {};
      (Array.isArray(u.data) ? u.data : []).forEach((row) => {
        if (row?.id) map[row.id] = { full_name: row.full_name, username: row.username };
      });
      setUserMap(map);
    } catch (e) {
      console.error('overview', e);
      showToast && showToast('Failed to load dashboard', 'err');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  // Resolve the CURRENT saved name for an activity row — not the snapshot
  // frozen at write-time. This is why "Faculty (default)" sometimes lingers
  // even after a profile edit. Fallback cascade: current full_name → current
  // username → snapshot user_name → '—'.
  const resolveName = useCallback((a) => {
    const live = a?.user_id && userMap[a.user_id];
    if (live?.full_name && live.full_name.trim()) return live.full_name;
    if (live?.username) return live.username;
    if (a?.user_name && a.user_name.trim()) return a.user_name;
    return '—';
  }, [userMap]);

  const staleReassignments = useMemo(
    () => pending.filter((o) => {
      if (!o?.created_at) return false;
      const d = new Date(o.created_at);
      return !Number.isNaN(d.getTime()) && (Date.now() - d.getTime()) > STALE_MS;
    }).length,
    [pending]
  );

  const downloadReport = async (format) => {
    try {
      const path = format === 'excel' ? endpoints.reports.overdueDefaultersExcel : endpoints.reports.overdueDefaultersPdf;
      const mime = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const r = await apiClient.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SAID_OutstandingReturns_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { showToast && showToast('Failed to download report', 'err'); }
  };

  // ────────────────────────────────────────────────────────────────────
  // Correct key mapping (backend truth → UI).
  // ────────────────────────────────────────────────────────────────────
  const u = stats.users || {};
  const inv = stats.inventory || {};
  const o = stats.orders || {};
  const rawActivities = Array.isArray(stats.recent_activities) ? stats.recent_activities : [];
  // Ensure deterministic chronological ordering even if the backend ever
  // returns a different sort — safety net, not a logic change.
  const activities = useMemo(
    () => [...rawActivities].sort(
      (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
    ),
    [rawActivities]
  );
  const operational = useMemo(() => activities.filter((a) => isOperational(a.action)), [activities]);
  const auxCount = activities.length - operational.length;

  // Prefer operational events; backfill with auxiliary so a brand-new
  // system doesn't show an empty feed. When backfilling we re-sort by
  // timestamp DESC so the mixed slice stays strictly chronological.
  const feed = useMemo(() => {
    if (operational.length >= 5) return operational.slice(0, 7);
    const mixed = [...activities];
    mixed.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
    return mixed.slice(0, 7);
  }, [operational, activities]);

  // Clickable only where a legit destination exists.
  const clickable = (destination) => ({
    onClick: () => goto && goto(destination),
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goto && goto(destination); } },
    role: 'button',
    tabIndex: 0,
    style: { cursor: 'pointer' },
  });

  return (
    <div data-testid="spa-overview-page">
      <div className="ap-eyebrow">SuperAdmin Console</div>
      <h1 className="ap-page-title">Overview</h1>
      <p className="ap-page-lede">
        System-wide health at a glance. Drill deeper into the Users, Reassign, or
        Activities pages for full CRUD, audit history, and faculty reassignments.
      </p>

      {/* ── SUMMARY TILES (clickable where a page exists) ───────────── */}
      <div
        className="ap-ind-row spa-clickable-row"
        role="group"
        aria-label="System summary"
        style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
      >
        <div
          className="ap-ind spa-ind-clickable"
          data-testid="spa-stat-users"
          aria-label={`${numFmt(u.total)} users. Open Users.`}
          {...clickable('users')}
        >
          <div className="ap-ind-label"><Users size={11} style={{ verticalAlign: -2 }} /> Users</div>
          <div className="ap-ind-value">{numFmt(u.total)}</div>
          <div className="ap-ind-hint">{numFmt(u.active)} active · {numFmt(u.blocked)} blocked</div>
        </div>

        <div
          className="ap-ind spa-ind-clickable"
          data-testid="spa-stat-components"
          aria-label={`${numFmt(inv.total_components)} components. Open Inventory.`}
          {...clickable('inventory')}
        >
          <div className="ap-ind-label"><Package size={11} style={{ verticalAlign: -2 }} /> Components</div>
          <div className="ap-ind-value">{numFmt(inv.total_components)}</div>
          <div className="ap-ind-hint">
            {numFmt(inv.total_stock_quantity)} units in stock
            {Number(inv.low_stock_count || 0) > 0 && (
              <> · <span style={{ color: '#B04A3C', fontWeight: 500 }}>{numFmt(inv.low_stock_count)} at/below threshold</span></>
            )}
          </div>
        </div>

        <div
          className="ap-ind spa-ind-clickable"
          data-testid="spa-stat-orders"
          aria-label={`${numFmt(o.total)} orders. Open Approval History.`}
          {...clickable('history')}
        >
          <div className="ap-ind-label"><ClipboardList size={11} style={{ verticalAlign: -2 }} /> Orders</div>
          <div className="ap-ind-value">{numFmt(o.total)}</div>
          <div className="ap-ind-hint">
            {numFmt(Number(o.pending_admin || 0) + Number(o.pending_superadmin || 0))} pending ·
            {' '}{numFmt(o.approved)} approved
          </div>
        </div>

        {/*
          Pending approval tile.
          The underlying endpoint `/api/superadmin/pending-approvals` returns
          every order with status "pending" — both those still awaiting the
          original faculty admin AND those explicitly reassigned to the
          SuperAdmin. We display `pending.length` directly and label it
          truthfully so SuperAdmins understand the scope.
        */}
        <div
          className="ap-ind spa-ind-clickable"
          data-testid="spa-stat-pending-approvals"
          aria-label={`${pending.length} pending approvals awaiting action. Open Reassign.`}
          {...clickable('reassign')}
        >
          <div className="ap-ind-label"><em className="is-exc" aria-label="Pending" /> Pending approval</div>
          <div className="ap-ind-value">{numFmt(pending.length)}</div>
          <div className="ap-ind-hint">
            Awaiting faculty or superadmin action
            {staleReassignments > 0 && (
              <> · <span style={{ color: '#B04A3C', fontWeight: 500 }}>{staleReassignments} &gt; 24h</span></>
            )}
          </div>
        </div>
      </div>

      {/* ── QUICK ACTIONS ──────────────────────────────────────────── */}
      <div
        className="ap-mt-4"
        role="group"
        aria-label="Quick actions"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10,
        }}
        data-testid="spa-quick-actions"
      >
        <button
          type="button"
          className="spa-quick-btn"
          onClick={() => goto && goto('inventory')}
          data-testid="spa-quick-inventory"
        >
          <span className="spa-quick-ic"><Package size={14} /></span>
          <span className="spa-quick-text">
            <span className="spa-quick-title">Manage Inventory</span>
            <span className="spa-quick-sub">Components · stock levels</span>
          </span>
          <ArrowRight size={14} className="spa-quick-arrow" />
        </button>
        <button
          type="button"
          className="spa-quick-btn"
          onClick={() => goto && goto('users')}
          data-testid="spa-quick-users"
        >
          <span className="spa-quick-ic"><Users size={14} /></span>
          <span className="spa-quick-text">
            <span className="spa-quick-title">Manage Users</span>
            <span className="spa-quick-sub">Faculty, clerks, students</span>
          </span>
          <ArrowRight size={14} className="spa-quick-arrow" />
        </button>
        <button
          type="button"
          className="spa-quick-btn"
          onClick={() => goto && goto('exchanges')}
          data-testid="spa-quick-exchanges"
        >
          <span className="spa-quick-ic"><Repeat2 size={14} /></span>
          <span className="spa-quick-text">
            <span className="spa-quick-title">Component Exchanges</span>
            <span className="spa-quick-sub">Pending supplier returns</span>
          </span>
          <ArrowRight size={14} className="spa-quick-arrow" />
        </button>
      </div>

      {staleReassignments > 0 && (
        <button
          type="button"
          className="ap-mt-3"
          onClick={() => goto && goto('reassign')}
          data-testid="spa-overview-stale-nudge"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            border: '1px solid rgba(176, 74, 60, 0.30)',
            background: 'rgba(176, 74, 60, 0.06)',
            borderRadius: 8,
            color: '#6B2E23',
            fontSize: 13,
            lineHeight: 1.5,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Clock size={18} style={{ color: '#B04A3C', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong>{staleReassignments} pending request{staleReassignments === 1 ? '' : 's'} waiting &gt; 24h.</strong>{' '}
            Reassign to another faculty before students escalate.
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B04A3C' }}>
            Open Reassign →
          </span>
        </button>
      )}

      {/* ── OUTSTANDING RETURNS (was: Defaulters) ──────────────────── */}
      <div className="ap-mt-6">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <div className="ap-eyebrow"><AlertOctagon size={11} style={{ verticalAlign: -2 }} /> Outstanding Returns</div>
            <h2 style={{ margin: '4px 0 0', fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 22, color: 'var(--diis-ink)' }}>
              {defaulters.count} case{defaulters.count === 1 ? '' : 's'} past the {defaulters.grace_days}-day grace
            </h2>
            <p className="ap-subtle" style={{ margin: '2px 0 0', fontSize: 12 }}>
              {defaulters.students_affected} student{defaulters.students_affected === 1 ? '' : 's'} affected
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => downloadReport('excel')} data-testid="spa-defaulter-download-excel">
              <Download size={14} style={{ marginRight: 4 }} /> Excel
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => downloadReport('pdf')} data-testid="spa-defaulter-download-pdf">
              <Download size={14} style={{ marginRight: 4 }} /> PDF
            </button>
          </div>
        </div>

        {loading ? (
          <div className="ap-empty">Loading…</div>
        ) : defaulters.cases.length === 0 ? (
          <div className="ap-empty" data-testid="spa-defaulter-empty">
            No outstanding returns. Every returnable item is within the grace window.
          </div>
        ) : (
          <div className="ap-table-wrap" data-testid="spa-defaulter-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Faculty</th>
                  <th>Patient · PID</th>
                  <th>Component</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th style={{ textAlign: 'right' }}>Days over</th>
                  <th>Extension</th>
                </tr>
              </thead>
              <tbody>
                {defaulters.cases.slice(0, 15).map((d) => (
                  <tr key={`${d.student_id}-${d.component_id}`} data-testid={`spa-defaulter-row-${d.student_id}-${d.component_id}`}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.student_name}</div>
                      <div className="ap-subtle">{d.student_category || '—'}{d.student_roll ? ` · Roll ${d.student_roll}` : ''}</div>
                    </td>
                    <td>{d.faculty_name || '—'}</td>
                    <td>
                      <div>{d.patient_names || '—'}</div>
                      <div className="ap-subtle">{d.patient_pids || '—'}</div>
                    </td>
                    <td>
                      <div>{d.component_name}</div>
                      <div className="ap-subtle">{d.brand_name || '—'}{d.size ? ` · ${d.size}` : ''}</div>
                    </td>
                    <td style={{ textAlign: 'right' }} className="cp-mono-num">{d.outstanding_qty}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ color: '#B04A3C', fontWeight: 600 }}>{d.days_overdue}</span>
                    </td>
                    <td className="ap-subtle" style={{ maxWidth: 220 }}>
                      {d.has_extension
                        ? `${d.extension_count}× · ${(d.latest_extension_reason_category || '').replace(/_/g, ' ')}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {defaulters.cases.length > 15 && (
              <div style={{ padding: 12, fontSize: 12, color: 'var(--diis-ink-subtle)', textAlign: 'center' }}>
                Showing 15 of {defaulters.cases.length}. Grant overrides from the Users page.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── RECENT ACTIVITY ────────────────────────────────────────── */}
      <div className="ap-mt-6">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <div className="ap-eyebrow"><Activity size={11} style={{ verticalAlign: -2 }} /> Recent Activity</div>
            <h2 style={{ margin: '4px 0 0', fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 22, color: 'var(--diis-ink)' }}>
              Operational events
            </h2>
            {auxCount > 0 && (
              <p className="ap-subtle" style={{ margin: '2px 0 0', fontSize: 12 }} data-testid="spa-recent-activity-aux-note">
                {auxCount} login / housekeeping event{auxCount === 1 ? '' : 's'} hidden here — fully visible in Activities.
              </p>
            )}
          </div>
          {activities.length > 0 && (
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={() => goto && goto('activities')}
              data-testid="spa-recent-activity-view-all"
            >
              View all <ArrowRight size={13} style={{ marginLeft: 4 }} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="ap-empty">Loading…</div>
        ) : feed.length === 0 ? (
          <div className="ap-empty" data-testid="spa-recent-activity-empty">
            No activity recorded yet. Audit events will appear here as users act on the system.
          </div>
        ) : (
          <ul
            data-testid="spa-recent-activity-list"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              border: '1px solid var(--diis-hairline)',
              borderRadius: 8,
              background: 'var(--diis-surface, white)',
            }}
          >
            {feed.map((a, idx) => {
              const aux = !isOperational(a.action);
              return (
                <li
                  key={a.id || idx}
                  data-testid={`spa-recent-activity-row-${a.id || idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 14px',
                    borderTop: idx === 0 ? 'none' : '1px solid var(--diis-hairline)',
                    fontSize: 13,
                    color: 'var(--diis-ink)',
                    opacity: aux ? 0.72 : 1,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>
                      <span data-testid={`spa-recent-activity-name-${a.id || idx}`}>{resolveName(a)}</span>{' '}
                      <span style={{ color: 'var(--diis-ink-subtle)', fontWeight: 400 }}>
                        · {actionLabel(a.action)}
                      </span>
                    </div>
                    {a.details && (
                      <div className="ap-subtle" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.details}
                      </div>
                    )}
                  </div>
                  <time
                    dateTime={a.timestamp || ''}
                    title={absTime(a.timestamp)}
                    className="ap-subtle"
                    style={{ fontSize: 11, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    <div>{relTime(a.timestamp)}</div>
                    {idx === 0 && (
                      <div style={{ fontSize: 10, opacity: 0.75 }}>{absTime(a.timestamp)}</div>
                    )}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Pending superadmin approvals (unchanged) ──────────────── */}
      {pending.length > 0 && (
        <div className="ap-mt-6">
          <div className="ap-eyebrow"><em className="is-exc" /> Pending approval</div>
          <h2 style={{ margin: '4px 0 8px', fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 22, color: 'var(--diis-ink)' }}>
            {pending.length} request{pending.length === 1 ? '' : 's'}
          </h2>
          <p className="ap-subtle" style={{ marginTop: 0, fontSize: 12 }}>
            Orders awaiting faculty or superadmin action. Use the Reassign page to move stuck requests between faculty admins.
          </p>
        </div>
      )}
    </div>
  );
}
